<template>
  <div
    :class="{
      'dialog-wrapper-root': true,
      'platform-dialog--compact': true,
      'platform-dialog--sized': heightPx != null,
      'platform-dialog--maximized': isMaximized,
    }"
  >
  <MDialog
    ref="dialogRef"
    v-model:visible="isVisible"
    :title="computedTitle"
    :width="`${widthPx}px`"
    :height="dialogHeight"
    :max-width="computedMaxWidth"
    :max-height="computedMaxHeight"
    :closable="closable"
    :dismissable-mask="false"
    :close-on-escape="false"
    :resizable="resizeEnabled"
    :data-testid="dialogTestId"
    @resize-start="onResizePointerDown"
    @hide="handleClose"
  >
    <template #header-actions>
      <template v-if="resizeEnabled">
        <Button
          type="button"
          icon="pi pi-window-maximize"
          text
          rounded
          severity="secondary"
          data-no-overlay-drag
          :disabled="isMaximized"
          :aria-label="t('ui.dialogExpand')"
          :title="t('ui.dialogExpand')"
          @click="expandToMax"
        />
        <Button
          type="button"
          icon="pi pi-window-minimize"
          text
          rounded
          severity="secondary"
          data-no-overlay-drag
          :disabled="isAtPreferred"
          :aria-label="t('ui.dialogCollapse')"
          :title="t('ui.dialogCollapse')"
          @click="collapseToPreferred"
        />
      </template>
    </template>
    <template #body>
      <MForm
        :key="`${data.id}:${dialogContentKey}`"
        :form-id="data.id"
        :session-id="dialogSessionId"
        :config="data.config"
      />
    </template>

    <template #footer v-if="showFooter">
      <div class="dialog-footer">
        <Button :label="t('dialogs.close')" severity="secondary" @click="handleClose" />
        <Button :label="t('dialogs.save')" @click="handleSave" v-if="showSaveButton" />
      </div>
    </template>
  </MDialog>
  </div>
</template>

<script setup lang="ts">
import { usePlatformDialogResize } from '@/common/composables/usePlatformDialogResize'
import { usePageData } from '@/common/composables/usePageData'
import { getFormTitle } from '@/common/utils/formTitle'
import {
  clampDialogWidth,
  maxResizableDialogHeight,
  maxResizableDialogWidth,
  nextGrowOnlyDialogHeight,
} from '@/common/utils/estimateFormContentWidth'
import { resolveDialogSlotRegistryFormId } from '@/common/utils/sameWindowFormRefetch'
import { resolveFormSchemaIdFromPageEntry } from '@/common/utils/resolvePageIdentity'
import { resolveFormLayoutObject } from '@/common/utils/reloadElementPageFocusGuard'
import { resolvePlatformDialogSize } from '@/common/utils/resolvePlatformDialogSize'
import { shouldSyncDialogSizeFromResolved } from '@/common/utils/shouldSyncDialogSizeFromResolved'
import { buildStableTestId } from '@/common/utils/testId'
import { useI18n } from '@/common/composables/useI18n'
import MForm from '@/components/platform/form/Form.vue'
import MDialog from '@/components/ui/MDialog.vue'
import { PlatformDialogLayoutKey, FormProviderKey, ReloadElementKey } from '@/components/platform/injection-keys'
import { actionService } from '@/services/action/action.service'
import { useAppStore } from '@/stores/app'
import Button from 'primevue/button'
import { computed, inject, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'

interface Props {
  data: {
    id: string
    sessionId: string
    config?: any
  }
  title?: string
  maxWidth?: string
  closable?: boolean
  showFooter?: boolean
  showSaveButton?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  closable: true,
  showFooter: false,
  showSaveButton: false
})

const emit = defineEmits<{
  close: []
  save: []
}>()

const appStore = useAppStore()
const { t } = useI18n()
const { pageData, pageFields } = usePageData()
const formProvider = inject(FormProviderKey, null)
const reloadElement = inject(ReloadElementKey, null)
const isVisible = ref(true)
const closedExplicitly = ref(false)
const dialogRef = ref<InstanceType<typeof MDialog> | null>(null)
const domRefined = ref(false)
const isMobile = ref(false)

const dialogConfig = computed(() => props.data?.config ?? {})
provide('dialogConfig', dialogConfig)

const registryFormId = computed(() => {
  return resolveDialogSlotRegistryFormId(
    pageData.value as Record<string, unknown> | null,
    props.data.id,
    appStore.dialogs?.[props.data.id]?.fid,
  )
})

const dialogSessionId = computed(() => {
  const fromPage = (pageData.value as Record<string, unknown> | undefined)?.sessionId
  if (fromPage != null && String(fromPage).trim() !== '') {
    return String(fromPage)
  }
  return props.data.sessionId
})

const dialogContentKey = computed(() => {
  const schemaId = resolveFormSchemaIdFromPageEntry(
    pageData.value as Record<string, unknown> | undefined,
    props.data.id,
  )
  return String(schemaId > 0 ? schemaId : registryFormId.value)
})

const viewport = () => ({
  width: typeof window !== 'undefined' ? window.innerWidth : 1920,
  height: typeof window !== 'undefined' ? window.innerHeight : 1080,
})

const resolvedDialogSize = computed(() =>
  resolvePlatformDialogSize(resolveFormLayoutObject(pageData.value), {
    formId: registryFormId.value,
    viewport: viewport(),
  }),
)

const preferredDialogSize = computed(() =>
  resolvePlatformDialogSize(resolveFormLayoutObject(pageData.value), {
    viewport: viewport(),
    ignoreSaved: true,
  }),
)

const {
  widthPx,
  heightPx,
  userResized,
  isMaximized,
  isAtPreferred,
  syncFromInitial,
  onResizePointerDown,
  expandToMax,
  collapseToPreferred,
} = usePlatformDialogResize({
  dialogEl: computed(() => dialogRef.value?.getDialogEl() ?? null),
  formId: registryFormId,
  initialWidthPx: computed(() => resolvedDialogSize.value.widthPx),
  initialHeightPx: computed(() => resolvedDialogSize.value.heightPx),
  preferredWidthPx: computed(() => preferredDialogSize.value.widthPx),
  preferredHeightPx: computed(() => preferredDialogSize.value.heightPx),
})

// Only when preferred size numbers change. Deep-watch on a new object each
// pageData touch (TEXT blur → setPageData) was resetting refined width/height.
watch(
  () => [resolvedDialogSize.value.widthPx, resolvedDialogSize.value.heightPx] as const,
  (next, prev) => {
    if (!shouldSyncDialogSizeFromResolved(next, prev)) {
      return
    }
    syncFromInitial()
  },
)

const platformDialogLayout = computed(() => ({
  compact: true,
  sized: heightPx.value != null,
}))
provide(PlatformDialogLayoutKey, platformDialogLayout)

const computedTitle = computed(() => {
  if (props.title) return props.title
  return getFormTitle({
    ...pageData.value,
    uniData: pageData.value?.uniData ?? { fields: { fields: pageFields.value || {} } }
  }) || t('dialogs.dialogFallbackTitle')
})

const dialogTestId = computed(() => buildStableTestId('dialog', [
  registryFormId.value
]))

const computedMaxWidth = computed(() => `${maxResizableDialogWidth(viewport())}px`)
const computedMaxHeight = computed(() => `${maxResizableDialogHeight(viewport())}px`)

const dialogHeight = computed(() => (heightPx.value ? `${heightPx.value}px` : undefined))

const resizeEnabled = computed(() => !isMobile.value)

const updateIsMobile = () => {
  isMobile.value = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
}

const refineWidthFromContent = async () => {
  if (domRefined.value || userResized.value) {
    return
  }

  await nextTick()
  requestAnimationFrame(() => {
    if (domRefined.value || userResized.value) {
      return
    }

    const contentEl = dialogRef.value?.getContentEl()
    if (!contentEl) {
      return
    }

    const scrollWidth = contentEl.scrollWidth
    const slack = 48
    if (scrollWidth > widthPx.value - slack) {
      widthPx.value = clampDialogWidth(scrollWidth + slack, viewport())
    }
    domRefined.value = true
  })
}

const refineHeightFromContent = async () => {
  if (userResized.value) {
    return
  }

  await nextTick()
  requestAnimationFrame(() => {
    if (userResized.value) {
      return
    }

    const dialogEl = dialogRef.value?.getDialogEl()
    const contentEl = dialogRef.value?.getContentEl()
    if (!dialogEl || !contentEl) {
      return
    }

    const headerH = dialogEl.querySelector('.p-dialog-header')?.getBoundingClientRect().height ?? 0
    const footerH = dialogEl.querySelector('.p-dialog-footer')?.getBoundingClientRect().height ?? 0
    const naturalHeight = Math.ceil(headerH + footerH + contentEl.scrollHeight + 24)
    const next = nextGrowOnlyDialogHeight({
      currentHeightPx: heightPx.value,
      naturalHeightPx: naturalHeight,
      viewport: viewport(),
      userResized: userResized.value,
    })
    if (next != null) {
      heightPx.value = next
    }
  })
}

watch(
  () => formProvider?.elementShellVisibleByElementId?.value,
  () => {
    void refineHeightFromContent()
  },
  { deep: true },
)

watch(
  dialogContentKey,
  () => {
    domRefined.value = false
    void refineWidthFromContent()
    void refineHeightFromContent()
  },
  { immediate: true },
)

const handleClose = async () => {
  if (closedExplicitly.value) {
    return
  }
  closedExplicitly.value = true
  isVisible.value = false

  try {
    const { default: router } = await import('@/router')
    await actionService.dismissDialogFromUi(props.data.id, {
      appStore,
      router,
      formProvider: formProvider ?? undefined,
      getForm: formProvider?.getForm,
      getElements: formProvider?.getElements,
      reloadElement: formProvider?.reloadElement ?? reloadElement ?? undefined,
      refillElement: formProvider?.refillElement,
    })
    emit('close')
  } catch (error) {
    console.error('Error closing dialog:', error)
    await appStore.removeDialog(props.data.id)
    emit('close')
  }
}

const handleSave = () => {
  emit('save')
}

onMounted(() => {
  updateIsMobile()
  window.addEventListener('resize', updateIsMobile)
  void refineWidthFromContent()
  void refineHeightFromContent()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile)
})
</script>

<style scoped lang="scss">
.dialog-footer {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 1rem 0 0 0;
}

.platform-dialog--compact :deep(.p-dialog-header) {
  padding: 0.75rem 0.75rem 0.4rem 0.75rem;
}

.platform-dialog--compact :deep(.p-dialog-content) {
  padding: 0.75rem;
}

.platform-dialog--compact :deep(.p-dialog-footer) {
  padding: 0.5rem 0.75rem 0.75rem 0.75rem;
}

.platform-dialog--sized :deep(.p-dialog) {
  display: flex;
  flex-direction: column;
}

.platform-dialog--maximized :deep(.p-dialog.p-dialog) {
  width: calc(100vw - 32px) !important;
  max-width: calc(100vw - 32px) !important;
  height: calc(var(--app-viewport-height, 100vh) - 32px) !important;
  max-height: calc(var(--app-viewport-height, 100vh) - 32px) !important;
  position: relative !important;
  top: auto !important;
  left: auto !important;
  margin: 0 !important;
}

.platform-dialog--sized :deep(.p-dialog-content) {
  flex: 1 1 auto;
  min-height: 0;
}

.platform-dialog--sized :deep(.m-dialog-content-shell),
.platform-dialog--sized :deep(.m-dialog-body-wrap),
.platform-dialog--sized :deep(.dialog-content) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.platform-dialog--sized :deep(.m-dialog-body-wrap) {
  overflow-x: hidden;
  overflow-y: auto;
}

/* No leftover table: content may be taller than the dialog (2673). Scroll the
 * body wrap like Swing page JScrollPane; do not flex-shrink children to the frame. */
.platform-dialog--sized:not(:has(.modern-table--fill)):not(:has(.modern-tree-table--fill))
  :deep(.m-dialog-body-wrap) {
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
}

.platform-dialog--sized:not(:has(.modern-table--fill)):not(:has(.modern-tree-table--fill))
  :deep(.dialog-content) {
  flex: 0 0 auto;
  min-height: min-content;
  height: auto;
  display: block;
}

.platform-dialog--compact :deep(.p-datatable-thead th),
.platform-dialog--compact :deep(.p-treetable-thead th) {
  padding: 0.35rem 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.25;
}

.platform-dialog--compact :deep(.p-datatable-tbody td),
.platform-dialog--compact :deep(.p-treetable-tbody td) {
  padding: 0.35rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.35;
}

:deep(.p-dialog) {
  .p-dialog-header {
    border-bottom: 1px solid #e5e7eb;
    padding: 1.5rem 1.5rem 1rem 1.5rem;

    .p-dialog-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
    }
  }

  .p-dialog-content {
    overflow: hidden;
    background-color: #ffffff !important;
  }

  .p-dialog-footer {
    border-top: 1px solid #e5e7eb;
    padding: 1rem 1.5rem 1.5rem 1.5rem;
  }
}

@media (max-width: 768px) {
  :deep(.p-dialog) {
    width: 100vw !important;
    max-width: 100vw !important;
    height: 100dvh !important;
    max-height: 100dvh !important;
    margin: 0 !important;
    border-radius: 0 !important;

    .p-dialog-header,
    .p-dialog-content,
    .p-dialog-footer {
      padding: 0.875rem 1rem;
    }

    .p-dialog-content {
      flex: 1 1 auto;
      min-height: 0;
    }
  }
}
</style>

<style lang="scss">
html.platform-dialog-resizing,
html.platform-dialog-resizing * {
  cursor: nwse-resize !important;
  user-select: none !important;
}

html.platform-dialog-resizing .p-dialog-content {
  overflow: hidden !important;
  overscroll-behavior: contain;
}

html.platform-dialog-dragging,
html.platform-dialog-dragging * {
  user-select: none !important;
}

html.platform-dialog-dragging .p-dialog,
html.platform-dialog-dragging .p-dialog-mask {
  transition: none !important;
}
</style>
